import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import styled from 'styled-components';
import { formatEuro } from '../../utils/formatters';
import { CHEVRON_WIDTH, ROW_HEIGHT, ROW_INLINE_PADDING, WHEN_COLUMN_WIDTH } from './rowMetrics';
import { getCustomerName, getDeliveryTypeLabel, getOrderTimeLabel, OrderRow } from '../utils/orderRowFields';
import { OrderStatus, orderStatusTextColor } from '../utils/orderStatus';

type Props = {
  order: OrderRow;
  customerOrdersView?: boolean;
  /** Opens the tapped order in the details sheet. */
  onOrderSelect: (id: string) => void;
};

/**
 * One row of the phone orders list. Phones show no column headers, so every value has to label
 * itself, and the row does it by position: the left column is when and how much, the right column is
 * what and for whom, ending in the status. The whole row is one target rather than an expandable
 * button — everything worth scanning is on it, so there is nothing left to expand and a single tap
 * opens the order in the details sheet.
 *
 * The row carries no border or shadow of its own: a screenful of outlined cards was a stack of boxes
 * competing with the text inside them, so the list is banded by the hairline dividers on its items
 * instead. What says it can be tapped is the chevron at its right edge — the disclosure indicator
 * every phone list uses, and the one this app already uses on its other tappable rows. It is doing
 * work the hover tint cannot: hover does not exist on a touch device, and the focus ring only shows
 * up once someone has reached for a keyboard. Those two remain for the pointer, and the pressed tint
 * is the touch answer to them.
 *
 * The status is the row's one piece of colour, and plain text rather than the tinted pill this row
 * used to lead with: a pill put the loudest thing on the row around the time, which is the one value
 * that is the same shape on every order. Coloured text at the foot of the stack marks the orders that
 * want attention and leaves the rest quiet.
 */
export const OrderListRow: React.FC<Props> = ({ order, customerOrdersView, onOrderSelect }) => {
  const { t } = useTranslation();
  const time = getOrderTimeLabel(order, customerOrdersView);
  // Empty when the order carries a `deliveryType` the labels are not keyed on, and then the dash
  // separating it from the number would open the line with nothing in front of it.
  const method = getDeliveryTypeLabel(order, t);

  return (
    <Row onClick={() => onOrderSelect(String(order.id))}>
      <When>
        {/* Only the customer-orders view has one: it spans a history rather than a single day. */}
        {time.date ? <WhenDate>{time.date}</WhenDate> : null}
        <WhenTime $demoted={!!time.date}>{time.time}</WhenTime>
        <WhenPrice>{formatEuro(order.shoppingCartPrice?.grandTotal)}</WhenPrice>
        {time.isPreorder ? <PreorderTag>{t('orderList.Preorder')}</PreorderTag> : null}
      </When>
      <Details>
        <DetailsHead>
          {method ? <Method>{method}</Method> : null}
          <OrderId>{method ? `– #${order.id}` : `#${order.id}`}</OrderId>
        </DetailsHead>
        <Customer>{getCustomerName(order)}</Customer>
        <Status $status={order.orderStatusString}>{t(order.orderStatusString)}</Status>
      </Details>
      {/* Decorative: MUI icons are `aria-hidden`, so the button keeps its name from the text above. */}
      <ChevronSlot>
        <Chevron fontSize="small" />
      </ChevronSlot>
    </Row>
  );
};

/**
 * The two columns, and the row's own chrome.
 *
 * Full width rather than sized to its content, because a <button> sizes itself to what is inside it
 * and a row that did so would ellipsise at a different character from its neighbours — and because
 * the hover tint is meant to fill the list's sheet from edge to edge, which is also why the padding
 * is the row's rather than the list's.
 *
 * `type` is set here rather than at the call site: a bare <button> inside a form submits it, and that
 * is a property of this component rather than something each use of it should have to remember.
 */
const Row = styled.button.attrs({ type: 'button' as const })`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  width: 100%;
  min-height: ${ROW_HEIGHT};
  padding: ${ROW_INLINE_PADDING}px;
  transition: background-color 150ms ease;
  color: inherit;
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.03)};
  }

  /* The touch answer to the hover tint, which a phone never sees: the chevron says the row can be
     tapped, and this confirms that it was. Stronger than hover because it has to register under a
     fingertip that is covering the row at the time. */
  &:active {
    background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.06)};
  }

  /* Inset, so the divider above the row does not clip the ring's top edge. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: -2px;
  }

  /* Everything a <button> brings of its own and this row does not want. */
  border: none;
  background: none;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
`;

/**
 * The left column: when, then how much, then whatever marker the order carries.
 *
 * `flex-start` so the preorder tag's highlight hugs its word instead of stretching to the column's
 * full width, which would read as a bar rather than a label.
 */
const When = styled.div`
  display: flex;
  flex: 0 0 ${WHEN_COLUMN_WIDTH}px;
  flex-direction: column;
  align-items: flex-start;
`;

/**
 * The column's headline wherever it appears — which day an order belongs to is the coarser reading,
 * so when there is one it outranks the clock beneath it.
 *
 * A point smaller than the time's 15px, and deliberately: `D.M.YYYY` runs to ten characters, and at
 * 15px bold a December date fills `WHEN_COLUMN_WIDTH` to within a pixel, which a different system
 * font on another platform would overrun.
 */
const WhenDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 700;
`;

// Bold and large while it leads the column; demoted to a supporting line under a date, which only
// the customer-orders view has.
const WhenTime = styled.span<{ $demoted: boolean }>`
  font-size: ${({ theme, $demoted }) => theme.fontSizes[$demoted ? 'xs' : 'md']};
  font-weight: ${({ $demoted }) => ($demoted ? 400 : 700)};
  color: ${({ theme, $demoted }) => ($demoted ? theme.palette.text.secondary : 'inherit')};
`;

// Grey and a step under the body size, like every supporting line in this column: the headline above
// is what a kitchen scans for, and the rest should stay quiet beneath it.
const WhenPrice = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

/**
 * The preorder marker. The yellow is `PREORDER_HIGHLIGHT` — the same ground the grid's placed-time
 * cell and the details header use — so an order booked for later looks the same wherever it is read.
 *
 * The negative margin cancels the inline padding, so the word starts on the column's left edge and
 * lines up with the time and the price above it while the highlight still has room around the text.
 * Left padded and not shifted back, the marker hung a few pixels right of everything above it and the
 * column read as ragged.
 */
const PreorderTag = styled.span`
  margin-top: 2px;
  margin-left: -4px;
  padding: 1px 4px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.highlight.main};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
`;

// Takes the room the left column leaves. `min-width: 0` is what lets its lines ellipsise at all —
// without it a flex item floors at its content's width and the long names push the row wider.
const Details = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const DetailsHead = styled.div`
  display: flex;
  gap: 0 5px;
  min-width: 0;
  white-space: nowrap;
`;

const Method = styled.span`
  flex: 0 0 auto;
  font-weight: 700;
`;

// The one part of the head that may lose characters, and the least costly one to lose.
const OrderId = styled.span`
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
`;

// One line, like the two around it, so the list scans as an even stack rather than a ragged one.
const Customer = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/**
 * A point under the body size: the colour is what carries it, so it needs no weight of its own — and
 * at 13px the longer labels ("Maksu saatu liian myöhään") keep more of themselves before ellipsising.
 *
 * Takes the status rather than a colour, and resolves the one from the other here: the theme is
 * already in hand at this point, so reading it in the component and handing down a finished hex only
 * moved the lookup somewhere it had to be asked for with a hook.
 */
const Status = styled.span<{ $status: OrderStatus }>`
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme, $status }) => orderStatusTextColor(theme, $status)};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/**
 * The disclosure chevron's slot, out of the detail column so it can sit against the row's right edge
 * and be centred across however many lines that column takes.
 */
const ChevronSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  /* Centred on the row, while the two columns beside it stay top-aligned. */
  align-self: center;
  width: ${CHEVRON_WIDTH}px;
`;

const Chevron = styled(ChevronRight)`
  color: ${({ theme }) => theme.palette.primary.main};
`;
